import type { CompanySize } from './types';

/** Runtime mirror of the backend COMPANY_SIZES — keep in lockstep with auth/onboarding.constants.ts. */
export const COMPANY_SIZES: readonly CompanySize[] = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '500+',
];

// Runtime mirrors of the canonical org-onboarding lists in @examing/types
// (the web bundler can't value-import workspace TS; keys.parity.test.ts fails on drift).
export const ORG_USE_CASES = [
  'hiring',
  'employee_training',
  'compliance',
  'school',
  'training_center',
  'exam_prep',
  'certification',
  'other',
] as const;
export type OrgUseCase = (typeof ORG_USE_CASES)[number];

/** Written by the first onboarding (and still by the Telegram form); read as `employee_training`. */
export const LEGACY_ORG_USE_CASES = ['corporate'] as const;

export const ONBOARDING_ROLES = [
  'hr_recruiter',
  'ld_manager',
  'team_lead',
  'teacher',
  'center_owner',
  'methodologist',
  'other',
] as const;
export type OnboardingRole = (typeof ONBOARDING_ROLES)[number];

export const ASSESS_AREAS = [
  'sales',
  'accounting',
  'customer_support',
  'call_center',
  'software_dev',
  'logistics',
  'management',
  'general_aptitude',
  'onboarding',
  'product_knowledge',
  'safety',
  'compliance',
  'soft_skills',
  'languages',
  'math',
  'physics',
  'chemistry',
  'biology',
  'native_language',
  'english',
  'russian',
  'history',
  'informatics',
  'ielts',
  'sat',
  'cefr',
  'dtm',
  'milliy_sertifikat',
] as const;
export type AssessArea = (typeof ASSESS_AREAS)[number];
export const MAX_ASSESS_AREAS = 5;

export const GRADE_BANDS = ['1-4', '5-9', '10-11', 'university'] as const;
export type GradeBand = (typeof GRADE_BANDS)[number];

export const PRIMARY_GOALS = [
  'first_test',
  'from_document',
  'import_questions',
  'proctored_exam',
  'certificates',
  'invite_team',
] as const;
export type PrimaryGoal = (typeof PRIMARY_GOALS)[number];

export interface OrgOnboardingProfile {
  role?: OnboardingRole;
  assessAreas?: AssessArea[];
  gradeBand?: GradeBand;
  primaryGoal?: PrimaryGoal;
  updatedAt?: string;
}

// UI-only: which answers each use case offers. Every list is a subset of the
// canonical keys above, so the API accepts whatever is shown.
const HIRING_AREAS: readonly AssessArea[] = [
  'sales', 'accounting', 'customer_support', 'call_center', 'software_dev', 'logistics', 'management', 'general_aptitude',
];
const TRAINING_AREAS: readonly AssessArea[] = [
  'onboarding', 'product_knowledge', 'safety', 'compliance', 'soft_skills', 'languages',
];
const SUBJECT_AREAS: readonly AssessArea[] = [
  'math', 'physics', 'chemistry', 'biology', 'native_language', 'english', 'russian', 'history', 'informatics',
];
const EXAM_AREAS: readonly AssessArea[] = ['ielts', 'sat', 'cefr', 'dtm', 'milliy_sertifikat'];

export const ROLES_BY_USE_CASE: Record<OrgUseCase, readonly OnboardingRole[]> = {
  hiring: ['hr_recruiter', 'team_lead', 'other'],
  employee_training: ['ld_manager', 'hr_recruiter', 'team_lead', 'other'],
  compliance: ['ld_manager', 'hr_recruiter', 'team_lead', 'other'],
  school: ['teacher', 'methodologist', 'center_owner', 'other'],
  training_center: ['center_owner', 'teacher', 'methodologist', 'other'],
  exam_prep: ['teacher', 'center_owner', 'methodologist', 'other'],
  certification: ['ld_manager', 'hr_recruiter', 'center_owner', 'other'],
  other: ONBOARDING_ROLES,
};

export const AREAS_BY_USE_CASE: Record<OrgUseCase, readonly AssessArea[]> = {
  hiring: [...HIRING_AREAS, 'languages'],
  employee_training: TRAINING_AREAS,
  compliance: ['compliance', 'safety', 'onboarding', 'product_knowledge'],
  school: SUBJECT_AREAS,
  training_center: [...SUBJECT_AREAS, ...EXAM_AREAS],
  exam_prep: [...EXAM_AREAS, 'english', 'math'],
  certification: ['product_knowledge', 'safety', 'compliance', 'software_dev', 'languages'],
  other: [...TRAINING_AREAS, ...SUBJECT_AREAS.slice(0, 4), ...EXAM_AREAS.slice(0, 2)],
};

/** Use cases whose learners sit in grade bands. */
export const GRADE_BAND_USE_CASES: readonly OrgUseCase[] = ['school', 'training_center'];

/** A stored use case (current or legacy) as a current key, or null. Mirrors the API's normalizeOrgUseCase. */
export function normalizeOrgUseCase(value: string | null | undefined): OrgUseCase | null {
  if (!value) return null;
  if ((ORG_USE_CASES as readonly string[]).includes(value)) return value as OrgUseCase;
  return (LEGACY_ORG_USE_CASES as readonly string[]).includes(value) ? 'employee_training' : null;
}
